import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Inbox, RefreshCw, Search } from 'lucide-react';
import { readCkScope, scopeKey, useCkScope } from '../components/CkOperationsShared';
import { createReceivingApi } from './api';
import './receiving.css';

const statuses = { open: 'In progress', needs_correction: 'Correction requested', submitted: 'Awaiting review', confirmed: 'Confirmed', rejected: 'Rejected' };

export default function OperatorInbox({ userId }) {
  const scope = useCkScope();
  return <InboxWorkspace key={`${userId}:${scopeKey(scope)}`} userId={userId} scope={scope} />;
}

function InboxWorkspace({ userId, scope }) {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const alive = useRef(false), pending = useRef(null);
  const api = useMemo(() => createReceivingApi(() => ({ ...readCkScope(), userId })), [userId]);
  const active = useCallback(() => alive.current && scopeKey(readCkScope()) === scopeKey(scope), [scope]);
  const load = useCallback(async () => {
    if (!active()) return;
    pending.current?.abort();
    const controller = new AbortController(); pending.current = controller;
    setLoading(true); setError(''); setReceipts([]);
    try {
      const result = await api.receipts({ mine: true, status: status || undefined, limit: 100 }, { signal: controller.signal });
      if (active() && !controller.signal.aborted) setReceipts(result);
    } catch (e) { if (active() && !controller.signal.aborted) setError(e.message); }
    finally {
      if (pending.current === controller) pending.current = null;
      if (active() && !controller.signal.aborted) setLoading(false);
    }
  }, [active, api, status]);
  useEffect(() => {
    alive.current = true; load();
    return () => { alive.current = false; pending.current?.abort(); pending.current = null; };
  }, [load]);
  const query = search.trim().toLowerCase();
  const visible = receipts.filter(receipt => [receipt.po_number, receipt.po_id, receipt.supplier_name, receipt.review_note]
    .some(value => String(value || '').toLowerCase().includes(query)));
  return <main className="receiving-queue" aria-label="My receiving inbox">
    <header className="receiving-heading"><div><p className="receiving-eyebrow">Receiving</p><h1><Inbox size={26} aria-hidden="true" />My receipts</h1></div>
      <button type="button" title="Refresh receipts" aria-label="Refresh receipts" disabled={loading} onClick={load}><RefreshCw size={20} /></button></header>
    <nav className="receiving-links" aria-label="Receiving navigation"><a href="/m/today">Inbound deliveries</a></nav>
    <div className="receiving-toolbar"><label><Search size={18} aria-hidden="true" /><input type="search" aria-label="Search receipts" placeholder="Supplier, PO or review note" value={search} onChange={e => setSearch(e.target.value)} /></label>
      <select aria-label="Receipt status" value={status} onChange={e => setStatus(e.target.value)}><option value="">All statuses</option>{Object.entries(statuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
      {!loading && !error && <span role="status">{visible.length} of {receipts.length} loaded receipts</span>}
    </div>
    {loading && <p role="status">Loading your receipts...</p>}
    {error && <p role="alert" className="receiving-error">{error}</p>}
    {!loading && !error && <>
      {receipts.length === 100 && <p role="status">Showing the latest 100 receipts for this status. Older receipts are not loaded.</p>}
      {!visible.length && <p className="receiving-empty">{query ? 'No matching loaded receipts.' : 'No receipts for this status at this site.'}</p>}
      <ul className="receiving-inbox">{visible.map(receipt => <li key={receipt.id}>
        <div className="receiving-order-title"><strong>{receipt.supplier_name || 'Supplier not specified'}</strong><span className="receiving-status">{statuses[receipt.status] || receipt.status}</span></div>
        <a href={`/m/receipt/${encodeURIComponent(receipt.id)}`}>{receipt.po_number || receipt.po_id || receipt.id}</a>
        <p>Arrived: {receipt.arrived_at || 'Not recorded'}</p>
        {receipt.review_note && <p className={receipt.status === 'needs_correction' ? 'receiving-error' : undefined}>Review note: {receipt.review_note}</p>}
      </li>)}</ul>
    </>}
  </main>;
}
